import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Users2, ChevronRight } from "lucide-react";

export default function Customers() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", address: "" });
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = async () => { const { data } = await api.get("/customers"); setCustomers(data); };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try { await api.post("/customers", form); toast.success("Customer added"); setCreating(false); setForm({ name: "", phone: "", address: "" }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const saveEdit = async () => {
    try { await api.put(`/customers/${editing.id}`, { name: editing.name, phone: editing.phone, address: editing.address }); toast.success("Customer updated"); setEditing(null); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const doDelete = async () => {
    try {
      const { data } = await api.delete(`/customers/${deleting.id}`);
      toast.success(data.archived ? "Customer archived (sales kept safe)" : "Customer deleted");
      setDeleting(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-chivo font-black text-3xl text-slate-900">Customers</h1>
          <p className="text-slate-500 mt-1">Click a customer to view full history. Rates are entered when generating a bill.</p>
        </div>
        <Button data-testid="add-customer-button" className="bg-sky-600 hover:bg-sky-700" onClick={() => setCreating(true)}><Plus className="h-4 w-4 mr-1" /> Add Customer</Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
        <table className="w-full text-sm" data-testid="customers-table">
          <thead className="bg-slate-50 text-slate-500 text-left border-b border-slate-200">
            <tr><th className="p-3 font-semibold">Name</th><th className="p-3 font-semibold">Phone</th><th className="p-3 font-semibold">Address</th><th className="p-3 text-right font-semibold">Actions</th></tr>
          </thead>
          <tbody>
            {customers.map((c) => (
              <tr key={c.id} className="border-t border-slate-100 hover:bg-sky-50/40 transition-colors" data-testid={`customer-row-${c.id}`}>
                <td className="p-3">
                  <button className="font-medium text-sky-700 hover:text-sky-900 hover:underline flex items-center gap-1"
                    data-testid={`open-customer-${c.id}`} onClick={() => navigate(`/admin/customers/${c.id}`)}>
                    {c.name} <ChevronRight className="h-4 w-4" />
                  </button>
                </td>
                <td className="p-3 text-slate-500">{c.phone || "—"}</td>
                <td className="p-3 text-slate-500">{c.address || "—"}</td>
                <td className="p-3 text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" data-testid={`edit-customer-${c.id}`} onClick={() => setEditing({ ...c })}><Pencil className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" data-testid={`delete-customer-${c.id}`} className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => setDeleting(c)}><Trash2 className="h-4 w-4" /></Button>
                </td>
              </tr>
            ))}
            {customers.length === 0 && <tr><td colSpan={4} className="p-10 text-center text-slate-400"><Users2 className="h-8 w-8 mx-auto mb-2 opacity-40" />No customers yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent data-testid="create-customer-dialog">
          <DialogHeader><DialogTitle>Add Customer</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input data-testid="customer-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Phone</Label><Input data-testid="customer-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div><Label>Address</Label><Input data-testid="customer-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button><Button data-testid="save-customer-button" className="bg-sky-600 hover:bg-sky-700" onClick={create}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="edit-customer-dialog">
          <DialogHeader><DialogTitle>Edit Customer</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div><Label>Name</Label><Input data-testid="edit-customer-name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></div>
              <div><Label>Phone</Label><Input value={editing.phone} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} /></div>
              <div><Label>Address</Label><Input value={editing.address} onChange={(e) => setEditing({ ...editing, address: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button><Button data-testid="save-edit-customer-button" className="bg-sky-600 hover:bg-sky-700" onClick={saveEdit}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent data-testid="delete-customer-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove customer {deleting?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              If this customer has sales or saved bills, they will be <b>archived</b> (removed from the active list) and all history is kept safe. If they have no records, they will be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-customer-button" className="bg-red-600 hover:bg-red-700" onClick={doDelete}>Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
